import React, { useEffect, useState } from "react";
import APIService from "../../../modules/apiService";
import Spinner from "../../../components/spinner";
import CreateInvoiceModal from "./createInvoiceModal";
import ItemType from "./itemType";
import { useBillingContext } from "..";
import { formatDate, formatMoney, formatNumber } from "../../../utils/formatters";
import moment from "moment-timezone";

const api = new APIService();

export default function PendingBillings() {
  const { showErrorModal } = useBillingContext();
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);

  const params = Object.fromEntries(new URLSearchParams(window.location.search));

  const [filterForm, setFilterForm] = useState({
    q: params.q || "",
    start: params.start || moment().subtract(3, "month").format("YYYY-MM-01"),
    end: params.end || moment().format("YYYY-MM-DD"),
    pending_type: params.pending_type || "",
  });

  const [filters, setFilters] = useState({...filterForm});

  const handleFilterFormChange = (e) => {
    setFilterForm({...filterForm, [e.target.name]: e.target.value});
  };

  const toggleItemSelection = (item) => {
    setSelectedItems((prev) => {
      const updated = prev.includes(item) ? prev.filter((a) => a !== item) : [...prev, item];
      return updated.sort((a, b) => b.id - a.id);
    });
  };

  const toggleAllItems = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems([...items].sort((a, b) => b.id - a.id));
    }
  };

  const handleInvoiceSelected = () => {
    setShowCreateInvoiceModal(true);
  };

  const fetchItems = async () => {
    setIsLoading(true);
    setSelectedItems([]);

    try {
      const apiFilters = {
        ...filters,
        without_invoice: true,
      };

      if (filters.pending_type) {
        apiFilters.type = filters.pending_type;
      }

      const response = await api.getPendingBilling(apiFilters);

      if (response.ok) {
        const { data } = await response.json();
        setItems(data);
      } else {
        const { message } = await response.json();
        throw new Error(`Error al obtener los pendientes de facturación: ${message}`);
      }
    } catch (err) {
      console.error(err);
      showErrorModal(err.message);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, [filters]);

  return (
    <>
      <form
        className="form-inline m-3"
        onSubmit={(event) => {
          event.preventDefault();

          window.history.replaceState(null, "", `${window.location.pathname}?${new URLSearchParams(filterForm)}`);

          setFilters({...filterForm});
        }}
      >
        <input type="search" name="q" className="form-control mb-2 mr-sm-2" placeholder="Buscar documento o nombre..."
               value={filterForm.q} onChange={handleFilterFormChange} style={{width: '260px'}}/>
        <input type="date" name="start" className="form-control mb-2 mr-sm-2" value={filterForm.start}
               onChange={handleFilterFormChange} max={filterForm.end}/>
        <input type="date" name="end" className="form-control mb-2 mr-sm-2" value={filterForm.end} onChange={handleFilterFormChange}
               min={filterForm.start}/>
        <select name="pending_type" className="form-control mb-2 mr-sm-2" value={filterForm.pending_type} onChange={handleFilterFormChange}>
          <option value="">Todos</option>
          <option value="appointment">Turno</option>
          <option value="prescription">Receta</option>
          <option value="certificate">Certificados</option>
        </select>
        <button type="submit" className="btn btn-primary mb-2 mr-sm-2">
          Filtrar
        </button>
      </form>
      {isLoading && <Spinner />}
      {items?.length === 0 && <div className="p-3 text-muted text-center">No se encontraron pendientes de facturación</div>}
      {items?.length > 0 && (
        <>
          <table className="table table-hover m-0">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" onChange={toggleAllItems} checked={items.length > 0 && selectedItems.length === items.length} />
                </th>
                <th className="text-right">Id</th>
                <th className="text-right">Fecha</th>
                <th className="text-center">Tipo</th>
                <th>Paciente</th>
                <th className="text-right">Documento</th>
                <th className="text-right">Importe</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index} onClick={() => toggleItemSelection(item)} style={{ cursor: "pointer" }}>
                  <td>
                    <input type="checkbox" onClick={(e) => e.stopPropagation()} onChange={() => toggleItemSelection(item)} checked={selectedItems.includes(item)} />
                  </td>
                  <td className="text-right">{formatNumber(item.id, 0)}</td>
                  <td className="text-right">{formatDate(item.start)}</td>
                  <td className="text-center">
                    <ItemType type={item.type} />
                  </td>
                  <td>{item.patient ? item.patient.fullname : <small className="text-muted">Sin paciente</small>}</td>
                  <td className="text-right">{item.patient ? formatNumber(item.patient.identification, 0) : <small className="text-muted">Sin documento</small>}</td>
                  <td className="text-right">{formatMoney(item.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 p-3 text-right">
            <button className="btn btn-primary" onClick={handleInvoiceSelected} disabled={selectedItems.length === 0}>
              Facturar seleccionados
            </button>
          </div>
        </>
      )}
      {showCreateInvoiceModal && (
        <CreateInvoiceModal
          visible={showCreateInvoiceModal}
          onClose={() => {
            setShowCreateInvoiceModal(false);
          }}
          items={selectedItems}
          onInvoiced={(item) => {
            setItems((prev) => prev.filter((a) => a.id !== item.id));
            setSelectedItems((prev) => prev.filter((a) => a.id !== item.id));
          }}
          onSkip={(item) => {
            setSelectedItems((prev) => prev.filter((a) => a.id !== item.id));
          }}
          onComplete={() => {
            fetchItems();
          }}
        />
      )}
    </>
  );
}
